import { useState } from 'react'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import MenuIcon from '@mui/icons-material/Menu'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { NavLink, Outlet } from 'react-router'

import { can, logout } from '../auth/session'
import { usePortal } from '../auth/sessionContext'
import { AccountCard } from './AccountCard'
import { destinationsFor } from './navigation'

const DRAWER_WIDTH = 240



export function AppShell() {
  const { membership } = usePortal()
  const queryClient = useQueryClient()
  const theme = useTheme()
  // The breakpoint is the same one the layout uses below, so the drawer is
  // temporary exactly when it would otherwise cover the page.
  const wide = useMediaQuery(theme.breakpoints.up('md'))
  const [open, setOpen] = useState(false)

  const signOut = useMutation({
    mutationFn: logout,
    onSettled: () => queryClient.resetQueries(),
  })


  const navigation = (
    <Box component="nav" aria-label="بخش‌ها" sx={{ px: 1, py: 1.5 }}>
      <List disablePadding>
        {destinationsFor(membership)
          .filter((item) => item.capability === null || can(membership, item.capability))
          .map((item) => (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            end={item.end}
            // On a phone the drawer covers the page it has just navigated to,
            // so the menu would be a trap without this.
            onClick={() => setOpen(false)}
            sx={{
              borderRadius: 1,
              mb: 0.25,
              // The selected destination is stated twice — a tinted ground and
              // a bar down its leading edge — because a tint alone is a
              // colour-only signal, and the sidebar is where somebody checks
              // where they are.
              '&.active': {
                bgcolor: 'action.selected',
                fontWeight: 600,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  insetInlineStart: 0,
                  top: 8,
                  bottom: 8,
                  width: 3,
                  borderRadius: 3,
                  bgcolor: 'primary.main',
                },
              },
              position: 'relative',
            }}
          >
            <ListItemText primary={item.label} slotProps={{ primary: { variant: 'body2' } }} />
            </ListItemButton>
          ))}
      </List>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }} elevation={0}>
        <Toolbar sx={{ gap: 2 }}>
          {wide ? null : (
            <IconButton
              color="inherit"
              edge="start"
              aria-label="باز کردن فهرست بخش‌ها"
              onClick={() => setOpen((value) => !value)}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography variant="h6" component="h1" noWrap sx={{ flexGrow: 1 }}>
            سامانه خدمات API
          </Typography>

          {/* Identity and the account switcher live in the sidebar now, with
              the balance. The app bar carried a native select that looked like
              a form control in a place nothing else was editable. */}
          <Button color="inherit" onClick={() => signOut.mutate()} disabled={signOut.isPending}>
            خروج
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={wide ? 'permanent' : 'temporary'}
        open={wide || open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: wide ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <Toolbar />
        <AccountCard />
        {navigation}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, bgcolor: 'background.default' }}>
        <Toolbar />
        <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  )
}
